/**
 * Schema-context assembler (M5.md cost + PII controls, AC-5). Builds the
 * per-request `LLMContext` the planner sends to the model. It carries column
 * names, inferred types, cardinality, and — ONLY for low-cardinality
 * categoricals (≤ MAX_DISTINCT_IN_CONTEXT distinct values) — the distinct
 * value set, so the model can resolve "make West red" without ever seeing a
 * row. High-cardinality columns (names, addresses, emails — the PII risk) get
 * type only. Nothing here is persisted.
 */

import type { PointSet } from "@/lib/map/pointset";
import { MAX_DISTINCT_IN_CONTEXT, type LLMContext } from "./types";

/** Column types whose distinct values are safe/useful to enumerate (when few). */
const CATEGORICAL_TYPES = new Set(["text", "state", "boolean", "zip"]);

/**
 * Assemble the schema-only LLM context from the loaded point set. Distinct
 * values are computed from the actual data (so resolution is value-aware) but
 * only surfaced for low-cardinality categoricals — the cap is what keeps PII
 * (high-cardinality free-text like names/emails) out of the model payload.
 */
export function assembleContext(pointSet: PointSet): LLMContext {
  const columns = pointSet.columns.map((col) => {
    const distinct = new Set<string>();
    for (const p of pointSet.points) {
      const v = p.props[col.name];
      if (v) distinct.add(v);
      // Stop counting once we know it's high-cardinality — no need to hold them all.
      if (distinct.size > MAX_DISTINCT_IN_CONTEXT) break;
    }
    const cardinality = distinct.size;
    const enumerable = CATEGORICAL_TYPES.has(col.type) && cardinality <= MAX_DISTINCT_IN_CONTEXT;
    return {
      name: col.name,
      type: col.type,
      cardinality,
      ...(enumerable ? { distinctValues: [...distinct].sort() } : {}),
    };
  });

  return { columns, rowCount: pointSet.count };
}

/**
 * Render the context as compact text for the model prompt. Deterministic
 * ordering so identical datasets produce identical prompts (prompt-cache
 * friendly). Never emits a row.
 */
export function contextToPrompt(ctx: LLMContext): string {
  const lines = ctx.columns.map((c) => {
    const base = `- "${c.name}" (${c.type}, ${c.cardinality} distinct)`;
    return c.distinctValues ? `${base}: ${c.distinctValues.map((v) => JSON.stringify(v)).join(", ")}` : base;
  });
  return `Dataset: ${ctx.rowCount} rows.\nColumns:\n${lines.join("\n")}`;
}
